// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { __awaiter, __generator } from "tslib";
import { generateUuid } from "@azure/core-http";
import { CanonicalCode } from "@azure/core-tracing";
import { ContainerClient } from "./ContainerClient";
import { Blob, Container } from "./generated/src/operations";
import { StorageClientContext } from "./generated/src/storageClient";
import { createSpan } from "./utils/tracing";
/**
 * A client that manages leases for a {@link ContainerClient} or a {@link BlobClient}.
 *
 * @export
 * @class BlobLeaseClient
 */

var BlobLeaseClient =
/** @class */
function () {
  /**
   * Creates an instance of BlobLeaseClient.
   * @param {(ContainerClient | BlobClient)} client The client to make the lease operation requests.
   * @param {string} leaseId Initial proposed lease id.
   * @memberof BlobLeaseClient
   */
  function BlobLeaseClient(client, leaseId) {
    var clientContext = new StorageClientContext(client.url, client.pipeline.toServiceClientOptions());
    this._url = client.url;

    if (client instanceof ContainerClient) {
      this._containerOrBlobOperation = new Container(clientContext);
    } else {
      this._containerOrBlobOperation = new Blob(clientContext);
    }

    if (!leaseId) {
      leaseId = generateUuid();
    }

    this._leaseId = leaseId;
  }

  Object.defineProperty(BlobLeaseClient.prototype, "leaseId", {
    /**
     * Gets the lease Id.
     *
     * @readonly
     * @memberof BlobLeaseClient
     * @type {string}
     */
    get: function get() {
      return this._leaseId;
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(BlobLeaseClient.prototype, "url", {
    /**
     * Gets the url.
     *
     * @readonly
     * @memberof BlobLeaseClient
     * @type {string}
     */
    get: function get() {
      return this._url;
    },
    enumerable: true,
    configurable: true
  });
  /**
   * Establishes and manages a lock on a container for delete operations, or on a blob
   * for write and delete operations.
   * The lock duration can be 15 to 60 seconds, or can be infinite.
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-container
   * and
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-blob
   *
   * @param {number} duration Must be between 15 to 60 seconds, or infinite (-1)
   * @param {LeaseOperationOptions} [options={}] option to configure lease management operations.
   * @returns {Promise<LeaseOperationResponse>} Response data for acquire lease operation.
   * @memberof BlobLeaseClient
   */

  BlobLeaseClient.prototype.acquireLease = function (duration, options) {
    if (options === void 0) {
      options = {};
    }

    return __awaiter(this, void 0, void 0, function () {
      var _a, span, spanOptions, e_1;

      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            _a = createSpan("BlobLeaseClient-acquireLease", options.tracingOptions), span = _a.span, spanOptions = _a.spanOptions;
            _b.label = 1;

          case 1:
            _b.trys.push([1, 3, 4, 5]);

            return [4
            /*yield*/
            , this._containerOrBlobOperation.acquireLease({
              abortSignal: options.abortSignal,
              duration: duration,
              modifiedAccessConditions: options.conditions,
              proposedLeaseId: this._leaseId,
              spanOptions: spanOptions
            })];

          case 2:
            return [2
            /*return*/
            , _b.sent()];

          case 3:
            e_1 = _b.sent();
            span.setStatus({
              code: CanonicalCode.UNKNOWN,
              message: e_1.message
            });
            throw e_1;

          case 4:
            span.end();
            return [7
            /*endfinally*/
            ];

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * To change the ID of the lease.
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-container
   * and
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-blob
   *
   * @param {string} proposedLeaseId the proposed new lease Id.
   * @param {LeaseOperationOptions} [options={}] option to configure lease management operations.
   * @returns {Promise<LeaseOperationResponse>} Response data for change lease operation.
   * @memberof BlobLeaseClient
   */


  BlobLeaseClient.prototype.changeLease = function (proposedLeaseId, options) {
    if (options === void 0) {
      options = {};
    }

    return __awaiter(this, void 0, void 0, function () {
      var _a, span, spanOptions, response, e_2;

      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            _a = createSpan("BlobLeaseClient-changeLease", options.tracingOptions), span = _a.span, spanOptions = _a.spanOptions;
            _b.label = 1;

          case 1:
            _b.trys.push([1, 3, 4, 5]);

            return [4
            /*yield*/
            , this._containerOrBlobOperation.changeLease(this._leaseId, proposedLeaseId, {
              abortSignal: options.abortSignal,
              modifiedAccessConditions: options.conditions,
              spanOptions: spanOptions
            })];

          case 2:
            response = _b.sent();
            this._leaseId = proposedLeaseId;
            return [2
            /*return*/
            , response];

          case 3:
            e_2 = _b.sent();
            span.setStatus({
              code: CanonicalCode.UNKNOWN,
              message: e_2.message
            });
            throw e_2;

          case 4:
            span.end();
            return [7
            /*endfinally*/
            ];

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * To free the lease if it is no longer needed so that another client may
   * immediately acquire a lease against the container or the blob.
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-container
   * and
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-blob
   *
   * @param {LeaseOperationOptions} [options={}] option to configure lease management operations.
   * @returns {Promise<LeaseOperationResponse>} Response data for release lease operation.
   * @memberof BlobLeaseClient
   */


  BlobLeaseClient.prototype.releaseLease = function (options) {
    if (options === void 0) {
      options = {};
    }

    return __awaiter(this, void 0, void 0, function () {
      var _a, span, spanOptions, e_3;

      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            _a = createSpan("BlobLeaseClient-releaseLease", options.tracingOptions), span = _a.span, spanOptions = _a.spanOptions;
            _b.label = 1;

          case 1:
            _b.trys.push([1, 3, 4, 5]);

            return [4
            /*yield*/
            , this._containerOrBlobOperation.releaseLease(this._leaseId, {
              abortSignal: options.abortSignal,
              modifiedAccessConditions: options.conditions,
              spanOptions: spanOptions
            })];

          case 2:
            return [2
            /*return*/
            , _b.sent()];

          case 3:
            e_3 = _b.sent();
            span.setStatus({
              code: CanonicalCode.UNKNOWN,
              message: e_3.message
            });
            throw e_3;

          case 4:
            span.end();
            return [7
            /*endfinally*/
            ];

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * To renew the lease.
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-container
   * and
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-blob
   *
   * @param {LeaseOperationOptions} [options={}] Optional option to configure lease management operations.
   * @returns {Promise<LeaseOperationResponse>} Response data for renew lease operation.
   * @memberof BlobLeaseClient
   */


  BlobLeaseClient.prototype.renewLease = function (options) {
    if (options === void 0) {
      options = {};
    }

    return __awaiter(this, void 0, void 0, function () {
      var _a, span, spanOptions, e_4;

      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            _a = createSpan("BlobLeaseClient-renewLease", options.tracingOptions), span = _a.span, spanOptions = _a.spanOptions;
            _b.label = 1;

          case 1:
            _b.trys.push([1, 3, 4, 5]);

            return [4
            /*yield*/
            , this._containerOrBlobOperation.renewLease(this._leaseId, {
              abortSignal: options.abortSignal,
              modifiedAccessConditions: options.conditions,
              spanOptions: spanOptions
            })];

          case 2:
            return [2
            /*return*/
            , _b.sent()];

          case 3:
            e_4 = _b.sent();
            span.setStatus({
              code: CanonicalCode.UNKNOWN,
              message: e_4.message
            });
            throw e_4;

          case 4:
            span.end();
            return [7
            /*endfinally*/
            ];

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * To end the lease but ensure that another client cannot acquire a new lease
   * until the current lease period has expired.
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-container
   * and
   * @see https://docs.microsoft.com/en-us/rest/api/storageservices/lease-blob
   *
   * @static
   * @param {number} breakPeriod Break period
   * @param {LeaseOperationOptions} [options={}] Optional options to configure lease management operations.
   * @returns {Promise<LeaseOperationResponse>} Response data for break lease operation.
   * @memberof BlobLeaseClient
   */


  BlobLeaseClient.prototype.breakLease = function (breakPeriod, options) {
    if (options === void 0) {
      options = {};
    }

    return __awaiter(this, void 0, void 0, function () {
      var _a, span, spanOptions, operationOptions, e_5;

      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            _a = createSpan("BlobLeaseClient-breakLease", options.tracingOptions), span = _a.span, spanOptions = _a.spanOptions;
            _b.label = 1;

          case 1:
            _b.trys.push([1, 3, 4, 5]);

            operationOptions = {
              abortSignal: options.abortSignal,
              breakPeriod: breakPeriod,
              modifiedAccessConditions: options.conditions,
              spanOptions: spanOptions
            };
            return [4
            /*yield*/
            , this._containerOrBlobOperation.breakLease(operationOptions)];

          case 2:
            return [2
            /*return*/
            , _b.sent()];

          case 3:
            e_5 = _b.sent();
            span.setStatus({
              code: CanonicalCode.UNKNOWN,
              message: e_5.message
            });
            throw e_5;

          case 4:
            span.end();
            return [7
            /*endfinally*/
            ];

          case 5:
            return [2
            /*return*/
            ];
        }
      });
    });
  };

  return BlobLeaseClient;
}();

export { BlobLeaseClient };